import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button } from '@/components/shadcn/button';
import BadmintonEventHeader from '@/components/community/badminton/BadmintonEventHeader';
import BadmintonStatistikContent from '@/components/community/badminton/BadmintonStatistikContent';
import BadmintonFeedbackDialog from '@/components/community/badminton/BadmintonFeedbackDialog';
import BadmintonConfirmDialog from '@/components/community/badminton/BadmintonConfirmDialog';
import BadmintonEditPlayerSheet from '@/components/community/badminton/BadmintonEditPlayerSheet';
import type { BadmintonStatistikTab } from '@/components/community/badminton/BadmintonStatistikTabs';
import type { FlatBadmintonGame } from '@/components/community/badminton/badmintonListUtils';
import type { BadmintonGameListItem } from '@/services/badmintonService';
import badmintonService from '@/services/badmintonService';
import { useBadmintonStatistik } from '@/pages/community/useBadmintonStatistik';
import { hostPaths } from '@/utils/hostPaths';
import { clientPaths, isCustomerAppPath } from '@/utils/clientPaths';
import { downloadOrShareBlob } from '@/utils/downloadBlob';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { playersLabel } from '@/components/community/badminton/badmintonUiUtils';
import showToast from '@/components/ui/Toast';
import mabarService from '@/services/mabarService';
import { formatDateDDMMYYYY } from '@/utils/dateUtils';
import type { LeaderboardPlayerItem } from '@/components/ui/recap/LeaderboardRecapCard';

interface FeedbackState {
  icon: string;
  title: string;
  message: string;
}

export default function BadmintonStatistikPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isClient = isCustomerAppPath(location.pathname);
  const [tab, setTab] = useState<BadmintonStatistikTab>('games');
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FlatBadmintonGame | null>(null);
  const [lineup, setLineup] = useState<{ game: BadmintonGameListItem; gameNumber: number } | null>(
    null
  );
  const [savingLineup, setSavingLineup] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const data = useBadmintonStatistik(slug, isClient);

  useEffect(() => {
    const state = location.state as { feedback?: FeedbackState } | null;
    if (!state?.feedback) return;
    setFeedback(state.feedback);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  if (data.loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!data.event || !slug) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-gray-600">{data.errorMessage || 'Event tidak ditemukan'}</p>
        <Button className="mt-4" onClick={() => navigate(isClient ? '/event' : hostPaths.mabar.list)}>
          Kembali
        </Button>
      </div>
    );
  }

  const handleShare = async () => {
    if (!slug) return;
    const shareUrl = `${window.location.origin}/event/${slug}/badminton?tab=standings`;
    const title = `Klasemen Akhir - ${data.event?.name || 'Mabar Badminton'}`;
    const text = `Lihat klasemen akhir pada event ${data.event?.name || ''}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast.success('Link klasemen berhasil disalin ke clipboard!');
    } catch {
      showToast.error('Gagal menyalin link');
    }
  };

  const handleDownload = async () => {
    if (!slug || !data.event || downloading) return;
    if (!data.standings || data.standings.length === 0) {
      showToast.error('Belum ada data klasemen untuk diunduh');
      return;
    }

    setDownloading(true);
    try {
      const leaderboardItems: LeaderboardPlayerItem[] = data.standings.slice(0, 5).map((p, idx) => ({
        position: p.position || idx + 1,
        playerName: p.playerName,
        avatarUrl: p.avatarUrl,
        matches: p.gamesPlayed,
        value: p.pointsScored,
        wins: p.wins,
        winRate: Math.round(p.winRate),
      }));

      const firstPlayer = leaderboardItems[0];
      const rawFirst = data.standings[0];
      const eventDateFormatted = formatDateDDMMYYYY(data.event.event_date);
      const location = data.event.lokasi_arena || data.event.address || '';
      const sportCategory = data.event.sport_category?.name || 'Badminton';

      const blob = await mabarService.renderShareRecapV2(slug, {
        eventName: data.event.name,
        eventDateFormatted,
        stats: {
          goals: 0,
          assists: 0,
          matches: firstPlayer?.matches || 0,
          point: firstPlayer?.value || 0,
          point_loss: rawFirst?.pointsConceded || 0,
          wins: rawFirst?.wins || 0,
          win_rate: rawFirst?.winRate || 0,
          is_racket: true,
          rank: 1,
        },
        playerName: firstPlayer?.playerName || 'Pemain',
        playerAvatarUrl: firstPlayer?.avatarUrl,
        template: 'leaderboard_padel',
        sportCategory,
        location,
        standingsJson: JSON.stringify(leaderboardItems),
      });

      const filename = `klasemen-badminton-${slug}.png`;
      const result = await downloadOrShareBlob(blob, filename, { title: data.event.name });
      if (result === 'saved') {
        showToast.success('Gambar klasemen berhasil diunduh/disimpan ke Galeri');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal mengunduh gambar klasemen';
      showToast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const item = pendingDelete;
    setPendingDelete(null);
    try {
      await badmintonService.deleteGame(item.game.slug);
      await data.loadData();
      setFeedback({
        icon: 'mdi:trash-can-outline',
        title: `Hapus Game ${item.gameNumber}`,
        message: `Game ${item.gameNumber} (${playersLabel(item.game.sideAPlayers)} vs ${playersLabel(item.game.sideBPlayers)}) dihapus dari rekap. Statistik pemain ikut diperbarui.`,
      });
    } catch (err: unknown) {
      setFeedback({
        icon: 'mdi:alert-circle-outline',
        title: 'Gagal menghapus',
        message: getApiErrorMessage(err, 'Gagal menghapus pertandingan'),
      });
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-4">
      <BadmintonEventHeader event={data.event} />
      <BadmintonStatistikContent
        tab={tab}
        onTabChange={setTab}
        canEdit={data.canEdit}
        standings={data.standings}
        topMmr={data.topMmr}
        flatGames={data.flatGames}
        onTambahGame={() =>
          navigate(
            isClient ? clientPaths.event.badmintonTambahGame(slug) : hostPaths.mabar.badmintonTambahGame(slug)
          )
        }
        onEditScore={(item) =>
          navigate(
            isClient
              ? clientPaths.event.badmintonEditSkor(slug, item.game.slug)
              : hostPaths.mabar.badmintonEditSkor(slug, item.game.slug),
            { state: { gameNumber: item.gameNumber } }
          )
        }
        onEditPlayer={(game, gameNumber) => setLineup({ game, gameNumber })}
        onDelete={setPendingDelete}
        onShare={data.canEdit ? handleShare : undefined}
        onDownload={data.canEdit ? handleDownload : undefined}
        downloading={downloading}
      />
      <BadmintonFeedbackDialog
        open={feedback != null}
        icon={feedback?.icon || 'mdi:check'}
        title={feedback?.title || ''}
        message={feedback?.message || ''}
        onClose={() => setFeedback(null)}
      />
      <BadmintonConfirmDialog
        open={pendingDelete != null}
        title="Yakin hapus?"
        message={`Game ${pendingDelete?.gameNumber ?? ''} akan dihapus dari rekap.`}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
      />
      <BadmintonEditPlayerSheet
        open={lineup != null}
        game={lineup?.game ?? null}
        gameNumber={lineup?.gameNumber ?? 1}
        participants={data.participants}
        sessions={data.sessions}
        saving={savingLineup}
        onClose={() => setLineup(null)}
        onSave={async (sideA, sideB) => {
          if (!lineup) return;
          setSavingLineup(true);
          try {
            await badmintonService.updateGameLineup(lineup.game.slug, { sideA, sideB });
            setLineup(null);
            await data.loadData();
          } catch (err: unknown) {
            setFeedback({
              icon: 'mdi:alert-circle-outline',
              title: 'Gagal menyimpan',
              message: getApiErrorMessage(err, 'Gagal memperbarui susunan pemain'),
            });
          } finally {
            setSavingLineup(false);
          }
        }}
      />
    </div>
  );
}
